/**
 * The M8 5(a) mineral guarantee (MC-a3): at the start, at least `minerals.guarantee.midTitanium` titanium-signature
 * Mid Reach systems and `deepPlatinum` platinum-signature Deep systems have an online relay path from home.
 * A galaxy that already meets it is returned unchanged. On a shortfall the nearest eligible online systems (fewest
 * online hops; ties picked on `deriveSeed(galaxySeed, 'mineral-guarantee')` from an index-ordered pool) have their
 * signature primary converted and their `mineralWeights` recomputed. Anchors, vein systems, `oreWeights`, grades
 * and relays are never touched. Load-time code: allocation is fine here.
 *
 * 5(e) MC-e6b: after the refractory post-pass, at least `minerals.guarantee.deepRefractory` refractory-signature Deep
 * systems have an online path from home (`applyRefractoryGuarantee`). On a shortfall the nearest eligible online
 * systems (ties on `deriveSeed(galaxySeed, 'refractory-guarantee')`) are re-run through the refractory pass as a
 * refractory signature. Never a vein system (`archetypeExclude`) or an anchor; the M8 signature and grades stay put.
 */
import { logger } from '../core/logger'
import { createRng, nextFloat, type Rng } from '../core/rng'
import type { GalaxyConfig } from '../data/galaxyConfig'
import { applySystemRefractory, excluded, mineralMix } from './minerals'
import { bfsHops } from './relays'
import { deriveSeed } from './seed'
import type { MineralOre, RegionId, Relay, StarSystem, SystemSignature } from './types'
import { VEIN } from './veins'

/** Warning (with `{ region, ore, need, got }`) when too few eligible online systems exist to meet a guarantee. */
export const MINERAL_GUARANTEE_SHORT = 'galaxy: mineral guarantee not met'

interface Target {
  region: RegionId
  ore: MineralOre
  need: number
}

/** Online hop counts from home over the relays as built (no route is opened); -1 where offline-only. */
export function onlineHops(systems: readonly StarSystem[], relays: readonly Relay[], homeIndex: number): number[] {
  const index = new Map(systems.map((s) => [s.id, s.index]))
  const edges: [number, number][] = []
  for (const r of relays) {
    const a = index.get(r.a)
    const b = index.get(r.b)
    if (r.online && a !== undefined && b !== undefined) edges.push([a, b])
  }
  return bfsHops(systems.length, edges, homeIndex)
}

/** The guarantee targets from the config, in a fixed order (mid titanium, then deep platinum). */
function targetsOf(cfg: GalaxyConfig): Target[] {
  const g = cfg.minerals.guarantee
  return [
    { region: 'mid', ore: 'titanium', need: g.midTitanium },
    { region: 'deep', ore: 'platinum', need: g.deepPlatinum },
  ]
}

/** True when `sys` counts toward `t`: right region, online from home, and the ore is its signature primary. */
const meets = (sys: StarSystem, t: Target, hops: readonly number[]): boolean =>
  sys.region === t.region && hops[sys.index] >= 0 && sys.signature?.primary === t.ore

/** The signature with `ore` as primary; a secondary equal to `ore` swaps with the old primary. */
function converted(sig: SystemSignature, ore: MineralOre): SystemSignature {
  if (sig.secondary === ore) return { primary: ore, secondary: sig.primary }
  return sig.secondary === undefined ? { primary: ore } : { primary: ore, secondary: sig.secondary }
}

/** The system with its signature primary set to `ore` and every asteroid field's `mineralWeights` recomputed. */
function convertSystem(sys: StarSystem, ore: MineralOre, cfg: GalaxyConfig): StarSystem {
  if (sys.signature === undefined) return sys
  const signature = converted(sys.signature, ore)
  return {
    ...sys,
    signature,
    fields: sys.fields.map((f) =>
      f.kind === 'asteroid'
        ? { ...f, mineralWeights: mineralMix(cfg.minerals, sys.region, sys.star.class, signature, f.oreWeights) }
        : f,
    ),
  }
}

/** Picks the next system to convert: the fewest online hops, ties picked by `rng` from the index-ordered pool. */
function pickNearest(rng: Rng, pool: readonly StarSystem[], hops: readonly number[]): StarSystem {
  const best = Math.min(...pool.map((s) => hops[s.index]))
  const tied = pool.filter((s) => hops[s.index] === best)
  return tied[Math.floor(nextFloat(rng) * tied.length)]
}

/**
 * The systems the pass converts, in pick order, as `[index, ore]` (empty when the galaxy already meets every
 * guarantee). Eligible: in the target region, online from home, with a signature (an asteroid field), not an
 * anchor, not a tetherstone vein and not already converted.
 */
export function guaranteeConversions(
  galaxySeed: number,
  systems: readonly StarSystem[],
  relays: readonly Relay[],
  homeIndex: number,
  cfg: GalaxyConfig,
): [number, MineralOre][] {
  const hops = onlineHops(systems, relays, homeIndex)
  const picks: [number, MineralOre][] = []
  let rng: Rng | null = null
  for (const t of targetsOf(cfg)) {
    const got = systems.filter((s) => meets(s, t, hops)).length
    const missing = t.need - got
    if (missing <= 0) continue
    rng ??= createRng(deriveSeed(galaxySeed, 'mineral-guarantee'))
    let pool = systems.filter(
      (s) =>
        s.region === t.region &&
        hops[s.index] >= 0 &&
        s.signature !== undefined &&
        s.signature.primary !== t.ore &&
        s.anchor === null &&
        s.archetype !== VEIN &&
        !picks.some(([i]) => i === s.index),
    )
    let added = 0
    for (; added < missing && pool.length > 0; added++) {
      const pick = pickNearest(rng, pool, hops)
      picks.push([pick.index, t.ore])
      pool = pool.filter((s) => s !== pick)
    }
    if (added < missing) logger.warn(MINERAL_GUARANTEE_SHORT, { region: t.region, ore: t.ore, need: t.need, got: got + added })
  }
  return picks
}

/** The guarantee pass (MC-a3) after the minerals post-pass. Returns `systems` itself when nothing converts. */
export function applyMineralGuarantee(
  galaxySeed: number,
  systems: StarSystem[],
  relays: readonly Relay[],
  homeIndex: number,
  cfg: GalaxyConfig,
): StarSystem[] {
  const picks = guaranteeConversions(galaxySeed, systems, relays, homeIndex, cfg)
  if (picks.length === 0) return systems
  const ores = new Map(picks)
  return systems.map((sys) => {
    const ore = ores.get(sys.index)
    return ore === undefined ? sys : convertSystem(sys, ore, cfg)
  })
}

const REFRACTORY: MineralOre = 'refractory_ore'

/**
 * The systems the refractory guarantee converts (MC-e6b), in pick order, by index (empty when enough refractory-signature
 * Deep systems are already online from home). Eligible: Deep, online from home, with a signature (an asteroid field),
 * not an anchor, not of an archetype that excludes the ore (the tetherstone vein) and not already a refractory signature.
 */
export function refractoryConversions(
  galaxySeed: number,
  systems: readonly StarSystem[],
  relays: readonly Relay[],
  homeIndex: number,
  cfg: GalaxyConfig,
): number[] {
  const need = cfg.minerals.guarantee.deepRefractory ?? 0
  if (need <= 0) return []
  const hops = onlineHops(systems, relays, homeIndex)
  const online = (s: StarSystem): boolean => s.region === 'deep' && hops[s.index] >= 0 && s.signature !== undefined
  const got = systems.filter((s) => online(s) && s.signature?.refractory === true).length
  if (got >= need) return []
  const rng = createRng(deriveSeed(galaxySeed, 'refractory-guarantee'))
  let pool = systems.filter(
    (s) =>
      online(s) && s.signature?.refractory !== true && s.anchor === null && s.archetype !== VEIN && !excluded(cfg.minerals, s.archetype, REFRACTORY),
  )
  const picks: number[] = []
  while (got + picks.length < need && pool.length > 0) {
    const pick = pickNearest(rng, pool, hops)
    picks.push(pick.index)
    pool = pool.filter((s) => s !== pick)
  }
  if (got + picks.length < need)
    logger.warn(MINERAL_GUARANTEE_SHORT, {
      region: 'deep',
      ore: REFRACTORY,
      need,
      got: got + picks.length,
    })
  return picks
}

/**
 * The refractory guarantee (MC-e6b) after `applyRefractory`: `before` is the galaxy the refractory pass received, `after`
 * its output. A converted system is `before`'s re-run through the pass as a forced refractory signature, so its fields
 * carry the signature share exactly as a rolled one would. Returns `after` itself when nothing converts.
 */
export function applyRefractoryGuarantee(
  galaxySeed: number,
  before: readonly StarSystem[],
  after: StarSystem[],
  relays: readonly Relay[],
  homeIndex: number,
  cfg: GalaxyConfig,
): StarSystem[] {
  const picks = new Set(refractoryConversions(galaxySeed, after, relays, homeIndex, cfg))
  if (picks.size === 0) return after
  return after.map((sys, i) => (picks.has(sys.index) ? applySystemRefractory(galaxySeed, before[i], cfg.minerals, true) : sys))
}
